import React, { useEffect, useState } from "react";
import axios from "axios";
import { getCurrentUser } from "../api/authApi";
import { toast } from "react-toastify";
import { API_ENDPOINTS } from "../utils/apiEndpoints";
import { API_CONFIG } from "../config/api.config";
import { safeScrollToTop } from "../utils/scrollGuard";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    tags: "",
    status: "PUBLISHED",
    isFeatured: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [user, setUser] = useState(getCurrentUser());
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchPosts();
    const currentUser = getCurrentUser();
    setUser(currentUser);
    // Check if user is admin or owner
    if (
      currentUser &&
      (currentUser.role === "ADMIN" || currentUser.role === "OWNER")
    ) {
      setIsAdmin(true);
    }
  }, []);

  const fetchPosts = async (page = 0, query = "") => {
    try {
      if (query) {
        const res = await axios.get(API_ENDPOINTS.BLOG_SEARCH(query));
        setPosts(res.data);
        setTotalPages(1);
        setCurrentPage(0);
      } else {
        const res = await axios.get(
          `${API_CONFIG.BASE_URL}/blog/paginated?page=${page}&size=5`
        );
        setPosts(res.data.posts);
        setCurrentPage(res.data.currentPage);
        setTotalPages(res.data.totalPages);
        setTotalItems(res.data.totalItems);
        setHasNext(res.data.hasNext);
        setHasPrevious(res.data.hasPrevious);
      }
    } catch (err) {
      console.error("Error fetching posts:", err);
      toast.error("Failed to fetch blog posts");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value) {
      fetchPosts(0, e.target.value);
    } else {
      fetchPosts(0);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchPosts(newPage);
    safeScrollToTop({ top: 0, behavior: "smooth" });
  };

  const openPostModal = (post) => {
    setSelectedPost(post);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closePostModal = () => {
    setShowModal(false);
    setSelectedPost(null);
    document.body.style.overflow = "unset";
  };

  const handleAddPost = async () => {
    if (!user || !user.user_id) {
      toast.warning("Please login to add a post.");
      return;
    }

    if (!isAdmin) {
      toast.error("Only admins and owners can post to the blog");
      return;
    }

    if (!newPost.title || !newPost.content) {
      toast.warning("Please fill in all required fields");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", newPost.title);
      formData.append("content", newPost.content);
      formData.append("tags", newPost.tags);
      formData.append("status", newPost.status);
      formData.append("isFeatured", newPost.isFeatured);
      formData.append("author", user.name);
      formData.append("userId", user.user_id);
      if (imageFile) {
        formData.append("image", imageFile);
      }

      await axios.post(API_ENDPOINTS.BLOG_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Blog post added successfully!");
      setShowAdd(false);
      setNewPost({
        title: "",
        content: "",
        tags: "",
        status: "PUBLISHED",
        isFeatured: false,
      });
      setImageFile(null);
      fetchPosts(currentPage);
    } catch (err) {
      console.error("Error adding post:", err);
      if (err.response && err.response.data && err.response.data.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Error adding post!");
      }
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: "linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        padding: 0,
        margin: 0,
      }}
    >
      {/* Modern Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FF5722 100%)",
          color: "#fff",
          padding:
            window.innerWidth < 768
              ? "50px 16px 30px 16px"
              : "60px 20px 40px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(238, 46, 36, 0.2)",
        }}
      >
        {/* Decorative Elements */}
        <div
          style={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.08)",
            animation: "float 6s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.06)",
            animation: "float 8s ease-in-out infinite",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <h1
            style={{
              fontSize:
                window.innerWidth < 768
                  ? window.innerWidth < 480
                    ? "1.8rem"
                    : "2.2rem"
                  : "2.8rem",
              fontWeight: "900",
              marginBottom: 12,
              paddingTop: window.innerWidth < 768 ? "20px" : "30px",
              letterSpacing: "-1px",
              textShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            📖 Trivana Blog
          </h1>
          <p
            style={{
              fontSize: window.innerWidth < 768 ? "0.95rem" : "1.1rem",
              opacity: 0.95,
              fontWeight: "500",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: "1.6",
              padding: window.innerWidth < 768 ? "0 8px" : "0",
            }}
          >
            Discover travel stories, insider tips, and hospitality insights
          </p>
        </div>
      </div>

      <div
        style={{
          maxWidth: 1100,
          margin: window.innerWidth < 768 ? "20px auto" : "40px auto",
          padding: window.innerWidth < 768 ? "0 12px" : "0 24px",
        }}
      >
        {/* Search Bar */}
        <div style={{ marginBottom: window.innerWidth < 768 ? 20 : 32 }}>
          <input
            type="text"
            placeholder={
              window.innerWidth < 480
                ? "🔍 Search..."
                : "🔍 Search by title, author, or tags..."
            }
            value={search}
            onChange={handleSearch}
            style={{
              width: "100%",
              padding: window.innerWidth < 768 ? "12px 16px" : "16px 24px",
              borderRadius: window.innerWidth < 768 ? 10 : 12,
              border: "2px solid #E5E7EB",
              fontSize: window.innerWidth < 768 ? "0.95rem" : "1rem",
              background: "#FFFFFF",
              transition: "all 0.3s ease",
              outline: "none",
              fontWeight: "500",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#EE2E24";
              e.target.style.boxShadow =
                "0 0 0 4px rgba(238, 46, 36, 0.08), 0 4px 12px rgba(0,0,0,0.08)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
            }}
          />
        </div>

        {/* Admin Add Post Button */}
        {isAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            style={{
              marginBottom: window.innerWidth < 768 ? 20 : 32,
              padding: window.innerWidth < 768 ? "10px 20px" : "14px 32px",
              background: "linear-gradient(135deg, #EE2E24 0%, #FF5722 100%)",
              color: "#fff",
              borderRadius: window.innerWidth < 768 ? 10 : 12,
              border: "none",
              cursor: "pointer",
              fontSize: window.innerWidth < 768 ? "0.9rem" : "1rem",
              fontWeight: "700",
              boxShadow: "0 6px 20px rgba(238, 46, 36, 0.3)",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: window.innerWidth < 480 ? "100%" : "auto",
              justifyContent: window.innerWidth < 480 ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 25px rgba(238, 46, 36, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 6px 20px rgba(238, 46, 36, 0.3)";
            }}
          >
            ✍️ Create New Post
          </button>
        )}

        {/* Add Post Form */}
        {showAdd && (
          <div
            style={{
              background: "#fff",
              padding: 36,
              borderRadius: 16,
              marginBottom: 40,
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
              border: "2px solid rgba(238, 46, 36, 0.15)",
            }}
          >
            <h3
              style={{
                fontSize: "1.8rem",
                fontWeight: "800",
                color: "#2E2E2E",
                marginBottom: 28,
                letterSpacing: "-0.5px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              ✨ Create New Post
            </h3>

            <input
              placeholder="Post Title *"
              value={newPost.title}
              onChange={(e) =>
                setNewPost({ ...newPost, title: e.target.value })
              }
              style={{
                width: "100%",
                marginBottom: 18,
                padding: "16px 20px",
                borderRadius: 12,
                border: "2px solid #E5E7EB",
                fontSize: "1rem",
                background: "#F9FAFB",
                transition: "all 0.3s ease",
                outline: "none",
                fontWeight: "500",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#EE2E24";
                e.target.style.background = "#FFFFFF";
                e.target.style.boxShadow = "0 0 0 4px rgba(238, 46, 36, 0.08)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.background = "#F9FAFB";
                e.target.style.boxShadow = "none";
              }}
            />

            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontWeight: "600",
                  color: "#2E2E2E",
                  fontSize: "0.95rem",
                }}
              >
                📷 Upload Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0])}
                style={{
                  width: "100%",
                  padding: "16px 20px",
                  borderRadius: 12,
                  border: "2px solid #E5E7EB",
                  fontSize: "0.95rem",
                  background: "#F9FAFB",
                  cursor: "pointer",
                }}
              />
            </div>

            <input
              placeholder="Tags (comma separated, e.g., travel, tips, guide)"
              value={newPost.tags}
              onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
              style={{
                width: "100%",
                marginBottom: 18,
                padding: "16px 20px",
                borderRadius: 12,
                border: "2px solid #E5E7EB",
                fontSize: "1rem",
                background: "#F9FAFB",
                transition: "all 0.3s ease",
                outline: "none",
                fontWeight: "500",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#EE2E24";
                e.target.style.background = "#FFFFFF";
                e.target.style.boxShadow = "0 0 0 4px rgba(238, 46, 36, 0.08)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.background = "#F9FAFB";
                e.target.style.boxShadow = "none";
              }}
            />

            <textarea
              placeholder="Content (Full blog post content) *"
              value={newPost.content}
              onChange={(e) =>
                setNewPost({ ...newPost, content: e.target.value })
              }
              style={{
                width: "100%",
                marginBottom: 24,
                padding: "16px 20px",
                borderRadius: 12,
                border: "2px solid #E5E7EB",
                fontSize: "1rem",
                background: "#F9FAFB",
                transition: "all 0.3s ease",
                outline: "none",
                fontWeight: "500",
                minHeight: "200px",
                resize: "vertical",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#EE2E24";
                e.target.style.background = "#FFFFFF";
                e.target.style.boxShadow = "0 0 0 4px rgba(238, 46, 36, 0.08)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.background = "#F9FAFB";
                e.target.style.boxShadow = "none";
              }}
            />

            <div style={{ display: "flex", gap: 14 }}>
              <button
                onClick={handleAddPost}
                style={{
                  background:
                    "linear-gradient(135deg, #EE2E24 0%, #FF5722 100%)",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "14px 32px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "1rem",
                  fontWeight: "700",
                  boxShadow: "0 6px 20px rgba(238, 46, 36, 0.3)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(238, 46, 36, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 6px 20px rgba(238, 46, 36, 0.3)";
                }}
              >
                ✅ Publish Post
              </button>
              <button
                onClick={() => setShowAdd(false)}
                style={{
                  padding: "14px 32px",
                  borderRadius: 12,
                  border: "2px solid #E5E7EB",
                  background: "#FFFFFF",
                  cursor: "pointer",
                  fontSize: "1rem",
                  fontWeight: "700",
                  color: "#2E2E2E",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#F3F4F6";
                  e.target.style.borderColor = "#9CA3AF";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#FFFFFF";
                  e.target.style.borderColor = "#E5E7EB";
                }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        )}

        {/* Blog Posts Grid */}
        <div style={{ display: "grid", gap: "28px", marginBottom: 40 }}>
          {posts.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              }}
            >
              <p
                style={{
                  fontSize: "1.2rem",
                  color: "#9CA3AF",
                  fontWeight: "500",
                }}
              >
                📝 No blog posts found. Be the first to create one!
              </p>
            </div>
          ) : (
            posts.map((post) => (
              <div
                key={post.id}
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  background: "#fff",
                  borderRadius: window.innerWidth < 768 ? 12 : 16,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  border: "2px solid #F3F4F6",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                }}
                onClick={() => openPostModal(post)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow =
                    "0 20px 40px rgba(238, 46, 36, 0.15)";
                  e.currentTarget.style.borderColor = "#EE2E24";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px rgba(0,0,0,0.08)";
                  e.currentTarget.style.borderColor = "#F3F4F6";
                }}
              >
                {post.coverImageUrl && (
                  <img
                    src={
                      post.coverImageUrl.startsWith("http")
                        ? post.coverImageUrl
                        : `${API_CONFIG.BASE_URL.replace("/api", "")}${
                            post.coverImageUrl
                          }`
                    }
                    alt={post.title}
                    onError={(e) => {
                      e.target.src =
                        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4";
                    }}
                    style={{
                      width: window.innerWidth < 768 ? "100%" : "320px",
                      height: window.innerWidth < 768 ? "200px" : "260px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div
                  style={{
                    padding: window.innerWidth < 768 ? "20px" : "32px 36px",
                    flex: 1,
                  }}
                >
                  <h2
                    style={{
                      color: "#1F2937",
                      fontWeight: 800,
                      fontSize: window.innerWidth < 768 ? "1.35rem" : "1.75rem",
                      marginBottom: window.innerWidth < 768 ? 8 : 12,
                      letterSpacing: "-0.5px",
                      lineHeight: "1.3",
                    }}
                  >
                    {post.title}
                  </h2>
                  <div
                    style={{
                      marginBottom: window.innerWidth < 768 ? 12 : 16,
                      fontSize: window.innerWidth < 768 ? "0.85rem" : "0.95rem",
                      color: "#6B7280",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: window.innerWidth < 768 ? 6 : 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>✍️</span>
                    <span style={{ color: "#EE2E24", fontWeight: 700 }}>
                      {post.author}
                    </span>
                    <span>•</span>
                    <span>📅</span>
                    <span>
                      {post.createdAt
                        ? new Date(post.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : ""}
                    </span>
                  </div>
                  <p
                    style={{
                      color: "#4B5563",
                      fontSize: window.innerWidth < 768 ? "0.95rem" : "1.05rem",
                      marginBottom: window.innerWidth < 768 ? 14 : 18,
                      lineHeight: "1.7",
                      fontWeight: "500",
                    }}
                  >
                    {post.content.substring(
                      0,
                      window.innerWidth < 768 ? 100 : 150
                    ) + "..."}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      marginBottom: 16,
                    }}
                  >
                    {post.tags &&
                      post.tags.split(",").map((tag) => (
                        <span
                          key={tag}
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(238, 46, 36, 0.08) 0%, rgba(255, 87, 34, 0.08) 100%)",
                            color: "#EE2E24",
                            borderRadius: 8,
                            padding: "6px 16px",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            border: "1.5px solid rgba(238, 46, 36, 0.2)",
                          }}
                        >
                          #{tag.trim()}
                        </span>
                      ))}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openPostModal(post);
                    }}
                    style={{
                      background:
                        "linear-gradient(135deg, #EE2E24 0%, #FF5722 100%)",
                      color: "#fff",
                      borderRadius: 10,
                      padding: "10px 24px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 16px rgba(238, 46, 36, 0.35)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(238, 46, 36, 0.25)";
                    }}
                  >
                    Read More →
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {!search && totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: window.innerWidth < 768 ? 10 : 16,
              marginTop: window.innerWidth < 768 ? 24 : 40,
              marginBottom: window.innerWidth < 768 ? 40 : 60,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!hasPrevious}
              style={{
                padding: window.innerWidth < 768 ? "10px 18px" : "12px 24px",
                borderRadius: window.innerWidth < 768 ? 10 : 12,
                border: "2px solid #E5E7EB",
                background: hasPrevious ? "#FFFFFF" : "#F3F4F6",
                color: hasPrevious ? "#2E2E2E" : "#9CA3AF",
                cursor: hasPrevious ? "pointer" : "not-allowed",
                fontSize: window.innerWidth < 768 ? "0.9rem" : "1rem",
                fontWeight: "700",
                transition: "all 0.3s ease",
                boxShadow: hasPrevious ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                flex: window.innerWidth < 480 ? "1" : "0",
              }}
              onMouseEnter={(e) => {
                if (hasPrevious) {
                  e.target.style.borderColor = "#EE2E24";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (hasPrevious) {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
                }
              }}
            >
              ← Previous
            </button>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(index)}
                  style={{
                    padding: "12px 20px",
                    borderRadius: 12,
                    border:
                      currentPage === index ? "none" : "2px solid #E5E7EB",
                    background:
                      currentPage === index
                        ? "linear-gradient(135deg, #EE2E24 0%, #FF5722 100%)"
                        : "#FFFFFF",
                    color: currentPage === index ? "#FFFFFF" : "#2E2E2E",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "700",
                    transition: "all 0.3s ease",
                    boxShadow:
                      currentPage === index
                        ? "0 4px 15px rgba(238, 46, 36, 0.3)"
                        : "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== index) {
                      e.target.style.borderColor = "#EE2E24";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== index) {
                      e.target.style.borderColor = "#E5E7EB";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
                    }
                  }}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNext}
              style={{
                padding: window.innerWidth < 768 ? "10px 18px" : "12px 24px",
                borderRadius: window.innerWidth < 768 ? 10 : 12,
                border: "2px solid #E5E7EB",
                background: hasNext ? "#FFFFFF" : "#F3F4F6",
                color: hasNext ? "#2E2E2E" : "#9CA3AF",
                cursor: hasNext ? "pointer" : "not-allowed",
                fontSize: window.innerWidth < 768 ? "0.9rem" : "1rem",
                fontWeight: "700",
                transition: "all 0.3s ease",
                boxShadow: hasNext ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                flex: window.innerWidth < 480 ? "1" : "0",
              }}
              onMouseEnter={(e) => {
                if (hasNext) {
                  e.target.style.borderColor = "#EE2E24";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (hasNext) {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
                }
              }}
            >
              Next →
            </button>
          </div>
        )}

        {/* Pagination Info */}
        {!search && totalPages > 0 && (
          <div
            style={{
              textAlign: "center",
              marginBottom: window.innerWidth < 768 ? 30 : 40,
              fontSize: window.innerWidth < 768 ? "0.85rem" : "0.95rem",
              color: "#6B7280",
              fontWeight: "500",
            }}
          >
            Showing page {currentPage + 1} of {totalPages} ({totalItems} total
            posts)
          </div>
        )}
      </div>

      {/* Blog Post Modal */}
      {showModal && selectedPost && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: window.innerWidth < 768 ? "flex-start" : "center",
            padding: window.innerWidth < 768 ? "8px" : "20px",
            overflow: "auto",
          }}
          onClick={closePostModal}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: window.innerWidth < 768 ? 16 : 20,
              maxWidth: "900px",
              width: "100%",
              maxHeight: window.innerWidth < 768 ? "100vh" : "90vh",
              overflow: "auto",
              position: "relative",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
              margin: window.innerWidth < 768 ? "0" : "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closePostModal}
              style={{
                position: "absolute",
                top: window.innerWidth < 768 ? 12 : 20,
                right: window.innerWidth < 768 ? 12 : 20,
                background: "rgba(238, 46, 36, 0.1)",
                border: "none",
                borderRadius: "50%",
                width: window.innerWidth < 768 ? 38 : 45,
                height: window.innerWidth < 768 ? 38 : 45,
                cursor: "pointer",
                fontSize: window.innerWidth < 768 ? "1.3rem" : "1.5rem",
                color: "#EE2E24",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#EE2E24";
                e.target.style.color = "#fff";
                e.target.style.transform = "rotate(90deg)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(238, 46, 36, 0.1)";
                e.target.style.color = "#EE2E24";
                e.target.style.transform = "rotate(0deg)";
              }}
            >
              ×
            </button>

            {/* Image */}
            {selectedPost.coverImageUrl && (
              <img
                src={
                  selectedPost.coverImageUrl.startsWith("http")
                    ? selectedPost.coverImageUrl
                    : `${API_CONFIG.BASE_URL.replace("/api", "")}${
                        selectedPost.coverImageUrl
                      }`
                }
                alt={selectedPost.title}
                onError={(e) => {
                  e.target.src =
                    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4";
                }}
                style={{
                  width: "100%",
                  height: "400px",
                  objectFit: "cover",
                  borderTopLeftRadius: 20,
                  borderTopRightRadius: 20,
                }}
              />
            )}

            {/* Content */}
            <div
              style={{
                padding: window.innerWidth < 768 ? "24px 20px" : "40px 48px",
              }}
            >
              <h1
                style={{
                  fontSize: window.innerWidth < 768 ? "1.6rem" : "2.5rem",
                  fontWeight: "900",
                  color: "#1F2937",
                  marginBottom: window.innerWidth < 768 ? 12 : 16,
                  letterSpacing: "-0.5px",
                  lineHeight: "1.2",
                }}
              >
                {selectedPost.title}
              </h1>

              <div
                style={{
                  marginBottom: window.innerWidth < 768 ? 18 : 24,
                  fontSize: window.innerWidth < 768 ? "0.9rem" : "1rem",
                  color: "#6B7280",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: window.innerWidth < 768 ? 8 : 12,
                  flexWrap: "wrap",
                }}
              >
                <span>✍️</span>
                <span style={{ color: "#EE2E24", fontWeight: 700 }}>
                  {selectedPost.author}
                </span>
                <span>•</span>
                <span>📅</span>
                <span>
                  {selectedPost.createdAt
                    ? new Date(selectedPost.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : ""}
                </span>
              </div>

              {selectedPost.tags && (
                <div
                  style={{
                    display: "flex",
                    gap: window.innerWidth < 768 ? 8 : 10,
                    flexWrap: "wrap",
                    marginBottom: window.innerWidth < 768 ? 20 : 32,
                  }}
                >
                  {selectedPost.tags.split(",").map((tag) => (
                    <span
                      key={tag}
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(238, 46, 36, 0.08) 0%, rgba(255, 87, 34, 0.08) 100%)",
                        color: "#EE2E24",
                        borderRadius: 8,
                        padding: "8px 18px",
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        border: "1.5px solid rgba(238, 46, 36, 0.2)",
                      }}
                    >
                      #{tag.trim()}
                    </span>
                  ))}
                </div>
              )}

              <div
                style={{
                  fontSize: window.innerWidth < 768 ? "1rem" : "1.15rem",
                  color: "#374151",
                  lineHeight: "1.8",
                  whiteSpace: "pre-wrap",
                  fontWeight: "500",
                }}
              >
                {selectedPost.content}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for float animation */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}
      </style>
    </div>
  );
};

export default Blog;
